import React from 'react';
import { Button, ButtonIcon, Icon, Typography } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';

import { type AddLicenseModalProps } from './type';

import './AddLicenseModal.css';

/**
 * @description Full-page modal prompting the user to add their first license.
 * Renders a centered card over a dark background when no licenses exist.
 * @param {AddLicenseModalProps} props - Component props
 * @param {() => void} props.onClose - Callback to close the modal
 * @param {() => void} props.onAddLicense - Callback when the user confirms adding a license
 * @param {() => void} props.onSkip - Callback when the user skips adding a license
 * @returns {React.ReactElement} The add license modal UI
 */

/**
 *
 */
function AddLicenseModal({
  onClose,
  onAddLicense,
  onSkip,
}: AddLicenseModalProps): React.ReactElement {
  const localize = useInvertedLocalize();

  return (
    <div className='LicenseManagement-AddLicenseModal' data-theme='dark'>
      <div className='LicenseManagement-AddLicenseModal-card'>
        <div className='LicenseManagement-AddLicenseModal-header'>
          <Typography
            kind='h2'
            className='LicenseManagement-AddLicenseModal-title'>
            {localize('Add License')}
          </Typography>
          <ButtonIcon
            size='medium'
            kind='ghost'
            icon={<Icon kind='Close' />}
            onClick={onClose}
            ariaLabel='Close Add License modal'
          />
        </div>

        <div className='LicenseManagement-AddLicenseModal-content'>
          <Typography
            kind='body2'
            className='LicenseManagement-AddLicenseModal-description'>
            {localize(
              "You haven't added any licenses yet. Add your first license to start tracking spend, utilization and optimization opportunities."
            )}
          </Typography>
        </div>

        <div className='LicenseManagement-AddLicenseModal-footer'>
          <Button kind='ghost' onClick={onSkip}>
            {localize('Skip for now')}
          </Button>
          <Button kind='filled' onClick={onAddLicense}>
            {localize('Add License')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AddLicenseModal;
